<template>
  <div>
    <admin-app-bar :class="hideAdminBar" />

    <v-container id="scheduler">
      <v-row no-gutters>
        <v-col class="pa-0" cols="12">
          <v-row class="fill-height" align="center" justify="center">
            <v-col cols="12" md="10" lg="10">
              <div v-if="days.length > 0" class="mt-5">
                <div class="mb-5">
                  <heading v-text="schedulerText" tag="h1"></heading>
                  <base-text v-text="schedulerSubText"></base-text>
                </div>
                <div>
                  <v-select
                    :items="datesAsNames"
                    :label="selectPlaceholder"
                    outlined
                    @change="daySelected($event)"
                    no-data-text="No dates currently available"
                    :disabled="datesAsNames.length == 0"
                  ></v-select>
                  <div v-if="datesAsNames.length == 0">
                    <v-banner elevation="2">
                      We don't have any days open at the moment. Please check
                      back soon!
                    </v-banner>
                  </div>
                  <day-component
                    :admin="admin"
                    :day="dynamicDayProps"
                    v-if="selectedDay"
                    @snackbarError="displaySnackbar"
                  />
                </div>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <v-snackbar v-model="snackbar" :timeout="timeout">
      {{ text }}

      <template v-slot:action="{ attrs }">
        <v-btn color="red" text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import moment from "moment";
import { db } from "../firebase/firebase";
import Day from "../components/Day";
import UtilsMixin from "../mixins/UtilsMixin";
import heading from "@/components/base/Heading";
import BaseText from "@/components/base/Text";
import AdminAppBar from "@/components/core/AdminAppBar";

export default {
  components: {
    "day-component": Day,
    heading,
    BaseText,
    AdminAppBar,
  },
  props: {
    admin: Boolean,
  },
  mixins: [UtilsMixin],
  data: () => ({
    weekdays: null,
    days: [],
    selectedDay: null,
    doneLoading: false,
    schedulerSubText:
      "Use the form below to select a date and time for your Virtual House Pro call.  All times shown are in the Central Time Zone.",
    snackbar: false,
    timeout: 2000,
    text: "Something went wrong, please try again...",
  }),
  computed: {
    hello() {
      return process.env.NODE_ENV;
    },
    hideAdminBar() {
      return this.admin ? "hidden" : "";
    },
    dayList() {
      return this.admin ? "days" : "daysThatHaveAvailability";
    },
    daysThatHaveAvailability() {
      return this.days.filter((day) => this.dayHasAvailableSlots(day.slots));
    },
    schedulerText() {
      return this.admin ? "Approve Time Slots" : "Schedule An Appointment";
    },
    datesAsNames() {
      return this[this.dayList].map((day) => this.convertToDate(day.date));
    },
    dynamicDayProps() {
      const [filteredDayProps] = this[this.dayList].filter(
        (day) => day.date === this.selectedDay
      );
      return filteredDayProps;
    },
    selectPlaceholder() {
      return this.datesAsNames.length > 0
        ? "Please select a day"
        : "No dates available currently";
    },
  },
  firestore() {
    return {
      calEvent: db.collection("calEvent"),
    };
  },
  methods: {
    displaySnackbar() {
      this.snackbar = true;
    },
    convertToDate(value) {
      const momentObj = moment(value, "MM-DD-YYYY");
      return momentObj.format("dddd MMMM Do, YYYY");
    },
    daySelected($event) {
      this.selectedDay = this.convertToDateString($event);
    },
    generateNextTwoWeekDateStrings() {
      let nextTwoWeeks = [];
      for (let i = 0; i < 15; i++) {
        let nextDay = moment().add(i, "days");
        nextTwoWeeks.push(nextDay.format("MM-DD-YYYY"));
      }
      return nextTwoWeeks;
    },
    generateSlots(numOfSlots = 12, hourStart = 8) {
      let items = [];
      new Array(numOfSlots).fill().forEach((acc, index) => {
        const slot = {
          starts_at: moment({ hour: index + hourStart }).format("h:mm A"),
          booked: false,
          approved: false,
        };
        items.push(slot);

        const slotPlus30Minutes = {
          starts_at: moment({ hour: index + hourStart, minute: 30 }).format(
            "h:mm A"
          ),
          booked: false,
          approved: false,
        };
        items.push(slotPlus30Minutes);
      });
      return items;
    },
    generateDays() {
      const calEventRef = db.collection("calEvent");
      this.weekdays = this.generateNextTwoWeekDateStrings();
      this.weekdays.forEach((day) => {
        this.$firestore.calEvent
          .doc(day)
          .get()
          .then((doc) => {
            // if no day is created in the database, create it here as well as generate slots
            if (!doc.data()) {
              calEventRef.doc(day).set(
                {
                  date: day,
                  slots: this.generateSlots(),
                },
                { merge: true }
              );
            }
          });
      });
    },
    dayHasAvailableSlots(slots) {
      const someAreAvailable = (slot) => slot.approved && !slot.booked;
      return slots.some(someAreAvailable);
    },
    init() {
      this.$firestore.calEvent.get().then((querySnapshot) => {
        const today = moment();
        querySnapshot.forEach((doc) => {
          const someday = moment(doc.data().date, "MM-DD-YYYY");
          if (someday.isAfter(today)) {
            this.days.push(doc.data());
          }
        });
        this.doneLoading = true;
      });
    },
  },
  watch: {
    doneLoading: function (newVal) {
      // newVal means this.doneLoading === true
      if (newVal && this.days.length < 12) {
        this.generateDays();
      }
    },
  },
  created() {
    this.init();
  },
};
</script>
<style lang="scss" scoped>
input {
  background-color: white;
  color: black;
}

.hidden {
  display: none;
}
</style>
